import { useState } from 'react';
import { z } from 'zod';
import type { Payment } from '@erp/rpc';
import { compareMoney } from '@erp/shared-utils';

const STORAGE_KEY = 'payment-detail-invoice-sort:v1';
const SortSchema = z.object({
  column: z.enum(['invoiceNumber', 'totalAmount', 'amount', 'dueDate']),
  direction: z.enum(['asc', 'desc']),
});

export type PaymentInvoiceSort = z.infer<typeof SortSchema>;
type PaymentInvoice = NonNullable<Payment['invoices']>[number];

function readSavedSort(): PaymentInvoiceSort | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return null;
    const parsed = SortSchema.safeParse(JSON.parse(saved));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

const invoiceNumberCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

function compareValues(
  left: string,
  right: string,
  column: PaymentInvoiceSort['column'],
) {
  switch (column) {
    case 'totalAmount':
    case 'amount':
      return compareMoney(left, right);
    case 'invoiceNumber':
      return invoiceNumberCollator.compare(left, right);
    case 'dueDate':
      return left.localeCompare(right);
  }
}

function sortInvoices(
  invoices: PaymentInvoice[],
  sort: PaymentInvoiceSort | null,
) {
  if (!sort) return invoices;
  const { column, direction } = sort;
  return invoices.toSorted((a, b) => {
    const left = a[column];
    const right = b[column];
    // Missing values stay last in both directions.
    if (left == null || left === '')
      return right == null || right === '' ? 0 : 1;
    if (right == null || right === '') return -1;
    const comparison = compareValues(left, right, column);
    return direction === 'asc' ? comparison : -comparison;
  });
}

export function usePaymentInvoiceSorting(invoices: PaymentInvoice[]) {
  const [sort, setSort] = useState(readSavedSort);

  function toggleSort(column: PaymentInvoiceSort['column']) {
    const next: PaymentInvoiceSort | null =
      sort?.column !== column
        ? { column, direction: 'asc' }
        : sort.direction === 'asc'
          ? { column, direction: 'desc' }
          : null;
    setSort(next);
    try {
      if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Sorting still works when browser storage is unavailable.
    }
  }

  return { sort, toggleSort, sortedInvoices: sortInvoices(invoices, sort) };
}
